// Pure model of the fiscal pages: RIB and IBAN display, the status of a version, the "new version"
// form (values, validation with the contract's RIB rule, request body) and the invoice series
// rules (code and prefix) with the field codes the API answers.
import type {
  CreateFiscalProfileDto,
  FiscalProfileListDtoOutputItemsItem,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { RibSchema } from '@batiflow/contracts';

export type FiscalProfile = FiscalProfileListDtoOutputItemsItem;

/** Tunisia has no daylight saving time: Africa/Tunis is UTC+01:00 all year. */
const TUNIS_OFFSET = '+01:00';

export const RIB_LENGTH = 20;

/** Digits only, at most 20 (what a RIB field keeps of whatever was typed or pasted). */
export const ribDigits = (input: string): string => input.replace(/\D/g, '').slice(0, RIB_LENGTH);

/** `08 006 0123456789012 34`: bank (2), branch (3), account (13), key (2). Works on partial input. */
export function groupRib(input: string): string {
  const digits = ribDigits(input);
  return [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5, 18), digits.slice(18, 20)]
    .filter(Boolean)
    .join(' ');
}

/** `TN59 0800 6012 3456 7890 1234`: groups of four. */
export function groupIban(iban: string): string {
  return (
    iban
      .replace(/\s+/g, '')
      .match(/.{1,4}/g)
      ?.join(' ') ?? ''
  );
}

export type VersionStatus = 'current' | 'scheduled' | 'past';

/** The version that applies now, one that starts later, or an earlier one. */
export function versionStatus(profile: FiscalProfile, now: Date): VersionStatus {
  if (profile.current) return 'current';
  return new Date(profile.effectiveFrom).getTime() > now.getTime() ? 'scheduled' : 'past';
}

/** The version that applies now, or the most recent one when none applies yet. */
export function currentVersion(profiles: readonly FiscalProfile[]): FiscalProfile | undefined {
  return profiles.find((profile) => profile.current) ?? profiles[0];
}

export interface NewVersionValues {
  /** 'now', or a chosen later date. */
  start: 'now' | 'later';
  /** `YYYY-MM-DD`, used when `start` is 'later'. */
  date: string;
  vatRegistered: boolean;
  taxRegime: 'real' | 'flat_rate';
  withholdingStatus: 'legal_entity' | 'real_regime' | 'other';
  invoiceFooter: string;
  bankName: string;
  /** Grouped digits as shown in the field. */
  rib: string;
}

/** The form starts from the version that applies now (a new version usually changes one thing). */
export function initialNewVersion(base: FiscalProfile | undefined): NewVersionValues {
  return {
    start: 'now',
    date: '',
    vatRegistered: base?.vatRegistered ?? true,
    taxRegime: base?.taxRegime ?? 'real',
    withholdingStatus: base?.withholdingStatus ?? 'legal_entity',
    invoiceFooter: base?.invoiceFooter ?? '',
    bankName: '',
    rib: '',
  };
}

export type NewVersionErrors = Partial<
  Record<'date' | 'bankName' | 'rib' | 'invoiceFooter', string>
>;

export const FOOTER_MAX = 1000;
export const BANK_NAME_MAX = 100;

/** The start of a calendar day in Tunisia, as an instant (ISO string, UTC). */
export function startOfTunisDay(date: string): string {
  return new Date(`${date}T00:00:00${TUNIS_OFFSET}`).toISOString();
}

/** Today's date in Tunisia, `YYYY-MM-DD` (the earliest start date of a later version). */
export function todayInTunis(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Tunis',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function validateNewVersion(
  values: NewVersionValues,
  now: Date,
  t: Translate,
): NewVersionErrors {
  const errors: NewVersionErrors = {};
  if (values.start === 'later') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(values.date))
      errors.date = t('fiscal.newVersion.errors.dateRequired');
    else if (values.date < todayInTunis(now)) errors.date = t('fiscal.newVersion.errors.datePast');
  }
  if (values.invoiceFooter.trim().length > FOOTER_MAX)
    errors.invoiceFooter = t('fiscal.newVersion.errors.footerTooLong', { max: FOOTER_MAX });
  const bankName = values.bankName.trim();
  const rib = ribDigits(values.rib);
  // Bank details come as a pair: the bank's name and its 20-digit RIB, or neither.
  if (bankName || rib) {
    if (!bankName) errors.bankName = t('fiscal.newVersion.errors.bankNameRequired');
    else if (bankName.length > BANK_NAME_MAX)
      errors.bankName = t('fiscal.newVersion.errors.bankNameTooLong', { max: BANK_NAME_MAX });
    if (!RibSchema.safeParse(rib).success) errors.rib = t('fiscal.newVersion.errors.ribInvalid');
  }
  return errors;
}

export function buildProfileBody(values: NewVersionValues): CreateFiscalProfileDto {
  const footer = values.invoiceFooter.trim();
  const bankName = values.bankName.trim();
  return {
    ...(values.start === 'later' ? { effectiveFrom: startOfTunisDay(values.date) } : {}),
    vatRegistered: values.vatRegistered,
    taxRegime: values.taxRegime,
    withholdingStatus: values.withholdingStatus,
    ...(footer ? { invoiceFooter: footer } : {}),
    ...(bankName ? { bankDetails: { bankName, rib: ribDigits(values.rib) } } : {}),
  };
}

// ---- Invoice series --------------------------------------------------------------------------

export const SERIES_CODE_PATTERN = /^[A-Z0-9]{1,10}$/;
export const SERIES_PREFIX_MAX = 20;

export interface SeriesValues {
  code: string;
  prefix: string;
  resetYearly: boolean;
  isDefault: boolean;
}

export type SeriesErrors = Partial<Record<'code' | 'prefix', string>>;

export function validateSeries(
  values: SeriesValues,
  t: Translate,
  options: { checkCode: boolean },
): SeriesErrors {
  const errors: SeriesErrors = {};
  if (options.checkCode && !SERIES_CODE_PATTERN.test(values.code.trim().toUpperCase()))
    errors.code = t('fiscal.series.errors.codeInvalid');
  const prefix = values.prefix.trim();
  if (!prefix) errors.prefix = t('fiscal.series.errors.prefixRequired');
  else if (prefix.length > SERIES_PREFIX_MAX)
    errors.prefix = t('fiscal.series.errors.prefixTooLong', { max: SERIES_PREFIX_MAX });
  return errors;
}

/** Whether a 422 carries a field error with this code (e.g. SERIES_CODE_TAKEN). */
export function hasFieldCode(error: unknown, code: string): boolean {
  return error instanceof ApiError && error.fieldErrors.some((field) => field.code === code);
}
